import React, { useState, useEffect, useRef } from 'react';
import { FaEye, FaEyeSlash, FaHistory, FaTrash } from 'react-icons/fa';

const SyncForm = ({ onStartSync, onViewLogs }) => {
  const [formData, setFormData] = useState({
    host1: '',
    port1: '',
    user1: '',
    password1: '',
    ssl1: true,
    host2: '',
    port2: '',
    user2: '',
    password2: '',
    ssl2: true,
    dryRun: true,
    delete1: false,
    automap: true,
    gmail1: false,
    gmail2: false
  });

  const [showPasswords, setShowPasswords] = useState({
    password1: false,
    password2: false
  });

  const isFirstRender = useRef(true);

  // Load saved form data
  useEffect(() => {
    const savedData = localStorage.getItem('imapsync_form_data');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
  }, []);

  // Save form data on change (skip initial render to avoid overwriting localStorage before load completes)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    localStorage.setItem('imapsync_form_data', JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const clearSavedData = () => {
    if (window.confirm('Clear all saved form data?')) {
      localStorage.removeItem('imapsync_form_data');
      setFormData({
        host1: '',
        port1: '',
        user1: '',
        password1: '',
        ssl1: true,
        host2: '',
        port2: '',
        user2: '',
        password2: '',
        ssl2: true,
        dryRun: true,
        delete1: false,
        automap: true,
        gmail1: false,
        gmail2: false
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    const required = ['host1', 'user1', 'password1', 'host2', 'user2', 'password2'];
    const missing = required.filter(field => !formData[field]);

    if (missing.length > 0) {
      alert(`Please fill in required fields: ${missing.join(', ')}`);
      return;
    }

    onStartSync(formData);
  };

  return (
    <div className="sync-form-container">
      <div className="form-header">
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={clearSavedData}
          >
            <FaTrash /> Clear Saved Data
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onViewLogs}
          >
            <FaHistory /> View Logs
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="sync-form">
        <div className="warning-box">
          <strong>⚠️ Important:</strong> This tool will sync emails from your source server to your destination server.
          Make sure you have proper backups before proceeding. Your credentials are processed securely and not stored.
        </div>

        {/* Source Server */}
        <div className="server-section">
          <h3>📨 Source Server (From)</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="host1">Server Address *</label>
              <input
                type="text"
                id="host1"
                name="host1"
                value={formData.host1}
                onChange={handleInputChange}
                placeholder="imap.gmail.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="port1">Port</label>
              <input
                type="number"
                id="port1"
                name="port1"
                value={formData.port1}
                onChange={handleInputChange}
                placeholder="993"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="user1">Username/Email *</label>
              <input
                type="text"
                id="user1"
                name="user1"
                value={formData.user1}
                onChange={handleInputChange}
                placeholder="your-email@gmail.com"
                required
              />
            </div>
            <div className="form-group password-group">
              <label htmlFor="password1">Password *</label>
              <div className="password-input">
                <input
                  type={showPasswords.password1 ? "text" : "password"}
                  id="password1"
                  name="password1"
                  value={formData.password1}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('password1')}
                >
                  {showPasswords.password1 ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          </div>
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="ssl1"
                checked={formData.ssl1}
                onChange={handleInputChange}
              />
              Use SSL/TLS (recommended)
            </label>
          </div>
        </div>

        {/* Destination Server */}
        <div className="server-section">
          <h3>📥 Destination Server (To)</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="host2">Server Address *</label>
              <input
                type="text"
                id="host2"
                name="host2"
                value={formData.host2}
                onChange={handleInputChange}
                placeholder="imap.example.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="port2">Port</label>
              <input
                type="number"
                id="port2"
                name="port2"
                value={formData.port2}
                onChange={handleInputChange}
                placeholder="993"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="user2">Username/Email *</label>
              <input
                type="text"
                id="user2"
                name="user2"
                value={formData.user2}
                onChange={handleInputChange}
                placeholder="your-email@example.com"
                required
              />
            </div>
            <div className="form-group password-group">
              <label htmlFor="password2">Password *</label>
              <div className="password-input">
                <input
                  type={showPasswords.password2 ? "text" : "password"}
                  id="password2"
                  name="password2"
                  value={formData.password2}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('password2')}
                >
                  {showPasswords.password2 ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          </div>
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="ssl2"
                checked={formData.ssl2}
                onChange={handleInputChange}
              />
              Use SSL/TLS (recommended)
            </label>
          </div>
        </div>

        {/* Sync Options */}
        <div className="server-section">
          <h3>⚙️ Sync Options</h3>
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="dryRun"
                checked={formData.dryRun}
                onChange={handleInputChange}
              />
              Dry run (test without actual sync)
            </label>
            <label>
              <input
                type="checkbox"
                name="delete1"
                checked={formData.delete1}
                onChange={handleInputChange}
              />
              Delete from source after sync
            </label>
            <label>
              <input
                type="checkbox"
                name="automap"
                checked={formData.automap}
                onChange={handleInputChange}
              />
              Auto-create missing folders
            </label>
            <label>
              <input
                type="checkbox"
                name="gmail1"
                checked={formData.gmail1}
                onChange={handleInputChange}
              />
              Source is Gmail
            </label>
            <label>
              <input
                type="checkbox"
                name="gmail2"
                checked={formData.gmail2}
                onChange={handleInputChange}
              />
              Destination is Gmail
            </label>
          </div>
        </div>

        <button type="submit" className="btn btn-primary submit-btn">
          🚀 Start Email Sync
        </button>
      </form>
    </div>
  );
};

export default SyncForm;